import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useT } from '../i18n';

interface SearchableSelectProps {
  value: string | number;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  onChange: (val: string | number) => void;
}

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (q[qi] === ' ') { qi++; continue; }
    if (t[ti] === q[qi]) qi++;
  }
  return qi >= q.length;
}

export function SearchableSelect({ value, options, placeholder, onChange }: SearchableSelectProps) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Rendered via a portal + fixed position (like InfoPopover) so the dropdown
  // never gets clipped by a modal's own overflow:auto body.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Close on scroll/resize of the page (fixed pos would drift) — but not on
    // scrolling the dropdown's own option list, which is itself a scroll
    // event that bubbles up to this window-level capture listener.
    const onMove = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(o => !o);
  };

  const filtered = useMemo(() => {
    if (!query) return options;
    return options.filter(o => fuzzyMatch(query, o.label));
  }, [options, query]);

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder || t('common.selectEllipsis');

  const select = (val: string | number) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="autosuggest-wrapper" ref={ref}>
      <div
        className="form-input"
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={toggle}
      >
        <span style={{ color: value ? 'var(--ink-1)' : 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel}
        </span>
        <ChevronDown size={14} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
      </div>
      {open && pos && createPortal(
        <div ref={dropdownRef} className="autosuggest-dropdown" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, right: 'auto', zIndex: 1000 }}>
          <input
            ref={inputRef}
            className="autosuggest-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filtered.length > 0) {
                select(filtered[0].value);
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
              }
            }}
            placeholder={t('dropdown.searchPlaceholder')}
            onClick={(e) => e.stopPropagation()}
          />
          {placeholder && (
            <div className="autosuggest-option" style={{ color: 'var(--ink-3)' }} onClick={() => select('')}>
              {placeholder}
            </div>
          )}
          {filtered.map((o) => (
            <div
              key={o.value}
              className="autosuggest-option"
              style={{ fontWeight: String(o.value) === String(value) ? 600 : 400 }}
              onClick={() => select(o.value)}
            >
              {o.label}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="autosuggest-option" style={{ color: 'var(--ink-3)', cursor: 'default' }}>
              {t('dropdown.noMatches')}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
